import { MAX_SPOKEN_CHARS } from '../utils/constants'
import { trimToSentence, sidePhrase } from '../utils/text'

/**
 * Build speech text from POI data
 */
export function buildSpeech(
  title: string,
  extract: string,
  meta: {
    headingKnown: boolean
    deltaSigned: number
    deltaAbs: number
  } | null,
  lang: string,
  extraPrefix?: string
): string {
  const cleanExtract = trimToSentence(extract, MAX_SPOKEN_CHARS)
  const parts: string[] = []

  if (meta && meta.headingKnown) {
    parts.push(sidePhrase(meta.deltaAbs, meta.deltaSigned, lang) + ':')
  }

  if (extraPrefix) parts.push(extraPrefix)
  parts.push(title + '.')
  if (cleanExtract) parts.push(cleanExtract)

  return parts.join(' ')
}

// Global reference to current utterance for proper cancellation
let currentUtterance: SpeechSynthesisUtterance | null = null

/**
 * Check if speech is currently active
 */
export function isSpeaking(): boolean {
  return currentUtterance !== null
}

/**
 * Speak text using Web Speech API
 * Properly cancels any ongoing speech before starting new one
 */
export function speakOnce(
  fullText: string,
  lang: string,
  onStart?: () => void,
  onEnd?: () => void,
  onError?: (error: Error) => void
): void {
  if (!fullText) return

  // Cancel any ongoing speech immediately and wait for it to stop
  try {
    if (window.speechSynthesis.speaking || window.speechSynthesis.pending) {
      window.speechSynthesis.cancel()
      // Small delay to ensure cancellation completes
      setTimeout(() => {
        startNewSpeech(fullText, lang, onStart, onEnd, onError)
      }, 50)
    } else {
      startNewSpeech(fullText, lang, onStart, onEnd, onError)
    }
  } catch (err) {
    console.error('Error in speakOnce:', err)
    // Try to start speech anyway
    startNewSpeech(fullText, lang, onStart, onEnd, onError)
  }
}

function startNewSpeech(
  fullText: string,
  lang: string,
  onStart?: () => void,
  onEnd?: () => void,
  onError?: (error: Error) => void
): void {
  try {
    // Create new utterance
    const utterance = new SpeechSynthesisUtterance(fullText)
    utterance.lang = lang || 'en'
    currentUtterance = utterance

    utterance.onstart = () => {
      onStart?.()
    }

    utterance.onend = () => {
      currentUtterance = null
      onEnd?.()
    }

    utterance.onerror = (e) => {
      currentUtterance = null
      onError?.(new Error(e.error || 'Speech synthesis error'))
    }

    window.speechSynthesis.speak(utterance)
  } catch (err) {
    console.error('Error starting speech:', err)
    currentUtterance = null
    onError?.(err instanceof Error ? err : new Error(String(err)))
  }
}

/**
 * Cancel any ongoing speech immediately
 */
export function cancelSpeech(): void {
  try {
    window.speechSynthesis.cancel()
    currentUtterance = null
  } catch (err) {
    console.error('Error cancelling speech:', err)
  }
}
